import { Component, ElementRef, ViewChild, signal, inject, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { doOnFirst } from '@app/shared/extra/utils';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { GaiaUserGuideService } from '@services/gaia-user-guide.service';
import { provideMarkdown, MARKED_OPTIONS, MarkedOptions } from 'ngx-markdown';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap, map, finalize } from 'rxjs/operators';

type ChatMessage = {
  id: string;
  author: 'user' | 'bot';
  text: string;
  streaming?: boolean;
  error?: boolean;
};

@Component({
  selector: 'gaia-user-guide-chat-widget',
  templateUrl: './gaia-user-guide-chat-widget.component.html',
  styleUrls: ['./gaia-user-guide-chat-widget.component.scss'],
  providers: [
    provideMarkdown(),
    {
      provide: MARKED_OPTIONS,
      useValue: {
        // handle generated links with external link icon
        renderer: {
          link: ({href, title, text}) => {
            const titleAttr = title ? ` title="${title}"` : '';
            return [
              `<span class="chat-link-wrapper">`,
              // Main link rendered from the markdown content.
              `<a href="${href}"${titleAttr}>${text}</a>`,
              // Secondary link that opens the same URL in a new tab.
              `<a href="${href}" target="_blank" rel="noopener noreferrer" class="chat-external-link" aria-label="Open in new tab">`,
              `<span class="material-icons">open_in_new</span>`,
              `</a>`,
              `</span>`
            ].join('');
          }
        }
      } as MarkedOptions
    },
  ],
  standalone: false
})
export class GaiaUserGuideChatWidgetComponent implements OnInit {

  protected readonly authService = inject(AuthService);
  private readonly gaiaUserGuideService = inject(GaiaUserGuideService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  protected readonly messages = signal<ChatMessage[]>([
    {
      id: crypto.randomUUID(),
      author: 'bot',
      text: this.translate.instant('MESSAGE.USER_GUIDE_WELCOME'),
    }
  ]);
  protected readonly isOpen = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly messageControl = new FormControl('', { nonNullable: true });
  private readonly sendQuestion$ = new Subject<string>();
  private readonly chatClosed$ = new Subject<void>();
  private chatId = crypto.randomUUID();

  @ViewChild('messagesContainer')
  private readonly messagesContainer?: ElementRef<HTMLDivElement>;

  /**
   * Initializes the component
   * Listens to user input, processes the input, interacts with a user guide service to fetch responses
   */
  public ngOnInit(): void {
    this.sendQuestion$.pipe(
      map(question => question.trim()),
      filter(question => !!question && !this.isLoading()),
      // Optimistically add the user's question to the chat and reset the input field.
      tap(question => {
        this.isLoading.set(true);
        this.messageControl.setValue('');
        this.messages.update(messages => [
          ...messages,
          {
            id: crypto.randomUUID(),
            author: 'user',
            text: question
          }
        ]);
        queueMicrotask(() => this.scrollToBottom());
      }),
      // Send the question to the guide service and stream the bot response.
      switchMap(question => {
        const botMessageId = crypto.randomUUID();
        return this.gaiaUserGuideService.askGaiaUserGuide(question, this.chatId)
          .pipe(
            // Cancel the stream immediately when the user closes the chat window.
            takeUntil(this.chatClosed$),
            // Once the first response chunk arrives, add an empty streaming bot message.
            doOnFirst(() => {
              this.isLoading.set(false);
              this.messages.update(messages => [
                ...messages,
                {
                  id: botMessageId,
                  author: 'bot',
                  text: '',
                  streaming: true
                }
              ]);
            }),
            // Append each streamed chunk to the latest bot message.
            tap(chunk => {
              this.messages.update(messages => {
                messages.last().text += chunk;
                return messages;
              });
              this.scrollToBottom();
            }),
            // Mark the bot message as complete when the stream ends or is interrupted.
            finalize(() => {
              this.messages.update(messages => {
                messages.last().streaming = false;
                return messages;
              });
              this.scrollToBottom();
            })
          );
      }),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe({
        // Show error message if the request fails
        error: () => {
          this.isLoading.set(false);
          this.messages.update(messages => [
            ...messages,
            {
              id: crypto.randomUUID(),
              author: 'bot',
              text: this.translate.instant('MESSAGE.USER_GUIDE_ERROR'),
              error: true
            }
          ]);
          queueMicrotask(() => this.scrollToBottom());
        }
      });
  }

  /**
   * Toggles the state of the chat between open and closed.
   * Closing resets the conversation; opening scrolls to the bottom asynchronously.
   */
  public toggleChat(): void {
    if (this.isOpen()) {
      this.closeChat();
    } else {
      this.isOpen.set(true);
      queueMicrotask(() => this.scrollToBottom());
    }
  }

  /**
   * Closes the chat window, cancels any in-flight request, and resets the conversation state
   * so the next session starts fresh with a new chat ID.
   */
  public closeChat(): void {
    this.chatClosed$.next();
    this.isOpen.set(false);
    this.chatId = crypto.randomUUID();
    this.messages.set([{
      id: crypto.randomUUID(),
      author: 'bot',
      text: this.translate.instant('MESSAGE.USER_GUIDE_WELCOME'),
    }]);
    this.messageControl.setValue('');
    this.isLoading.set(false);
  }

  /**
   * Sends a message by retrieving the value from the message control
   * and emitting it through the sendQuestion$ observable.
   */
  public sendMessage(): void {
    this.sendQuestion$.next(this.messageControl.value);
  }

  /**
   * Handles the Enter key press event
   * @param {KeyboardEvent} event - The keyboard event triggered by the Enter key press.
   */
  public onEnter(event: KeyboardEvent): void {
    if (event.shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  }

  /**
   * Scrolls the content of the messages container to the bottom.
   */
  private scrollToBottom(): void {
    const element = this.messagesContainer?.nativeElement;
    if (!element) {
      return;
    }
    element.scrollTop = element.scrollHeight;
  }
}
